import { FormControl, TextField, MenuItem, InputLabel, Select, Button } from '@mui/material'
import React, { useEffect, useState, useMemo } from 'react'
import { destinationTypeOptions } from '../../../constants/constantList';
import _, { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { MoreHorizOutlined } from '@mui/icons-material';
import WorkflowSessionModal from '../../ItemProperties/WorkflowSessionModal';

const ActiveMQ = ({ 
    handleBodyContent, 
    connectionDetails, 
    repoPDC = {}, 
    useConnection, 
    disableControl = false, 
    screen = '', 
}) => {
    const { t } = useTranslation()
    const [outTime, setOutTime] = useState('');
    const [contextUrl, setContextUrl] = useState('');
    const [destinationType, setDestinationType] = useState('QUEUE');
    const [destinationName, setDestinationName] = useState('');
    const [durableClientId, setDurableClientId] = useState('');
    const [content, setContent] = useState(null);
    const [valueLabel, setValueLabel] = useState("");
    const [workflowSessionModal, setWorkflowSessionModal] = useState({
        open: false,
        isFirstTime: false,
    });

    const workflowMappings = {
        [t("LBL_TIMEOUT")]: {
            stateSetter: setOutTime,
            key: "TIMEOUT",
            value: outTime,
        },
        [t("LBL_CONTEXT_URL")]: {
            stateSetter: setContextUrl,
            key: "INIT_URL",
            value: contextUrl,
        },
        [t("LBL_DESTINATION_NAME")]: {
            stateSetter: setDestinationName,
            key: "QUEUE_OR_TOPIC_NAME",
            value: destinationName,
        },
        [t("LBL_DURABLE_CLIENT_ID")]: {
            stateSetter: setDurableClientId,
            key: "DURABLE_SUBSCRIPTION_CLIENT_ID",
            value: durableClientId,
        },
    };

    const valuesHandler = (value) => {
        if (workflowMappings[valueLabel]) {
            const { stateSetter, key } = workflowMappings[valueLabel];
            stateSetter(value);
            onInputChange(key, value);
        }
    };

    const getFontWeight = (control, currentValue, useConnection) => {
        if (!repoPDC) return "normal";
        return repoPDC?.[control] !== currentValue &&
            useConnection === "preDefined"
            ? "bold"
            : "normal";
    };

    const fontWeights = useMemo(() => {
        return {
            timeout: getFontWeight("TIMEOUT", outTime, useConnection),
            contextUrl: getFontWeight("INIT_URL", contextUrl, useConnection),
            destinationName: getFontWeight("QUEUE_OR_TOPIC_NAME", destinationName, useConnection),
            durableClientId: getFontWeight("DURABLE_SUBSCRIPTION_CLIENT_ID", durableClientId, useConnection),
        };
    }, [outTime, contextUrl, destinationName, durableClientId, useConnection, repoPDC]);

    const destinationTypeDropDownItems = destinationTypeOptions.map((obj, key) => (
        <MenuItem key={key} value={obj.value}>
            {obj.text}
        </MenuItem>
    ));

    const debouncedContentUpdate = debounce(handleBodyContent, 500);

    const onInputChange = (name, value) => {
        if(['GET_DATA', 'SEND_DATA'].includes(screen)){
            setContent((prev) => ({ add:{ ...prev?.add,  [name]: value} }));
            return;
        }
        setContent((prev) => {
            return { ...prev, [name]: value };
        });
    }

    useEffect(() => {
        if(!_.isEqual(content, connectionDetails)) debouncedContentUpdate(content);
    }, [content]);

    useEffect(() => {
        setOutTime(connectionDetails?.TIMEOUT || '');
        setContextUrl(connectionDetails?.INIT_URL || '');
        setDestinationType(connectionDetails?.DESTINATION_TYPE || 'QUEUE');
        setDestinationName(connectionDetails?.QUEUE_OR_TOPIC_NAME || '');
        setDurableClientId(connectionDetails?.DURABLE_SUBSCRIPTION_CLIENT_ID || '');
        setContent({ ...connectionDetails })
    }, [connectionDetails]);

    return (
        <>
            <div style={{ display: "flex", gap: "4px" }}>
                <FormControl className="width-100" size="small">
                    <TextField
                        variant="outlined"
                        size="small"
                        label={t("LBL_TIMEOUT")}
                        type="text"
                        value={outTime}
                        disabled={disableControl}
                        inputProps={{
                            style: { fontWeight: fontWeights.timeout }
                        }}
                        onChange={(e) => {
                            const inputValue = e.target.value;
                            const numericValue = inputValue.replace(/\D/g, '');
                            if (numericValue.length <= 9) {
                                setOutTime(numericValue);
                                onInputChange("TIMEOUT", numericValue);
                            }
                        }}
                        className={"width-100"}
                        name={"TIMEOUT"}
                        autoComplete="off"
                    />
                </FormControl>
                {["GET_DATA", "SEND_DATA"].includes(screen) && (
                    <WorkflowSessionModalButton
                        disableControl={disableControl}
                        label={t("LBL_TIMEOUT")}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                    />
                )}
            </div>
            <div style={{ display: "flex", gap: "4px" }}>
                <FormControl className="width-100" size="small">
                    <TextField
                        variant="outlined"
                        size="small"
                        label={t("LBL_CONTEXT_URL")}
                        type="text"
                        value={contextUrl}
                        disabled={disableControl}
                        inputProps={{
                            style: { fontWeight: fontWeights.contextUrl }
                        }}
                        onChange={(e) => {
                            onInputChange("INIT_URL", e.target.value);
                            setContextUrl(e.target.value)
                        }}
                        className={"width-100"}
                        name={"INIT_URL"}
                        autoComplete="off"
                    />
                </FormControl>
                {["GET_DATA", "SEND_DATA"].includes(screen) && (
                    <WorkflowSessionModalButton
                        disableControl={disableControl}
                        label={t("LBL_CONTEXT_URL")}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                    />
                )}
            </div>
            <FormControl variant="outlined" className="width-100" size="small">
                <InputLabel id="destinationTypeDropdownLabel">
                    {t("LBL_DESTINATION_TYPE")}
                </InputLabel>
                <Select
                    className={"width-100"}
                    disabled={disableControl}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                maxHeight: 300,
                            },
                        },
                    }}
                    name={"DESTINATION_TYPE"}
                    labelId="destinationTypeDropdownLabel"
                    label={t("LBL_DESTINATION_TYPE")}
                    id="destinationTypeDropdown"
                    value={destinationType || ''}
                    onChange={(e) => {
                        const value = e.target.value;
                        onInputChange("DESTINATION_TYPE", value);
                        setDestinationType(value)
                        if (value === 'QUEUE') {
                            onInputChange('DURABLE_SUBSCRIPTION_CLIENT_ID', null);
                            setDurableClientId('')
                        }
                    }}
                >
                    {destinationTypeDropDownItems}
                </Select>
            </FormControl>
            <div style={{ display: "flex", gap: "4px" }}>
                <FormControl className="width-100" size="small">
                    <TextField
                        variant="outlined"
                        size="small"
                        label={t("LBL_DESTINATION_NAME")}
                        type="text"
                        value={destinationName || ''}
                        disabled={disableControl}
                        inputProps={{
                            style: { fontWeight: fontWeights.destinationName }
                        }}
                        onChange={(e) => {
                            onInputChange("QUEUE_OR_TOPIC_NAME", e.target.value);
                            setDestinationName(e.target.value)
                        }}
                        className={"width-100"}
                        name={"QUEUE_OR_TOPIC_NAME"}
                        autoComplete="off"
                    />
                </FormControl>
                {["GET_DATA", "SEND_DATA"].includes(screen) && (
                    <WorkflowSessionModalButton
                        disableControl={disableControl}
                        label={t("LBL_DESTINATION_NAME")}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                    />
                )}
            </div>
            {destinationType === 'TOPIC' && (
                <div style={{ display: "flex", gap: "4px" }}>
                    <FormControl className="width-100" size="small">
                        <TextField
                            variant="outlined"
                            size="small"
                            label={t("LBL_DURABLE_CLIENT_ID")}
                            type="text"
                            value={durableClientId || ''}
                            disabled={disableControl}
                            inputProps={{
                                style: { fontWeight: fontWeights.durableClientId }
                            }}
                            onChange={(e) => {
                                onInputChange("DURABLE_SUBSCRIPTION_CLIENT_ID", e.target.value || null);
                                setDurableClientId(e.target.value)
                            }}
                            className={"width-100"}
                            name={"DURABLE_SUBSCRIPTION_CLIENT_ID"}
                            autoComplete="off"
                        />
                    </FormControl>
                    {["GET_DATA", "SEND_DATA"].includes(screen) && (
                        <WorkflowSessionModalButton
                            disableControl={disableControl}
                            label={t("LBL_DURABLE_CLIENT_ID")}
                            setWorkflowSessionModal={setWorkflowSessionModal}
                            setValueLabel={setValueLabel}
                        />
                    )}
                </div>
            )}

            <WorkflowSessionModal
                valueLabel={valueLabel}
                showAccountData={true}
                showCallingWorkflow={true}
                showSystemData={true}
                showWorkflowSession={true}
                showDateAndTime={true}
                openModal={workflowSessionModal}
                setModalOpen={setWorkflowSessionModal}
                handleClose={() =>
                    setWorkflowSessionModal({ open: false, isFirstTime: false })
                }
                initialValue={workflowMappings[valueLabel]?.value}
                inputType="text"
                valuesHandler={(value) => {
                    valuesHandler(value);
                }}
            />
        </>
    )
}

export default ActiveMQ

const WorkflowSessionModalButton = ({ setWorkflowSessionModal, label, disableControl, setValueLabel }) => {
    return (
        <Button
            size="small"
            disabled={disableControl}
            variant="outlined"
            onClick={() => {
                setValueLabel(label);
                setWorkflowSessionModal({ open: true, isFirstTime: true });
            }}
            sx={{ width: "40px", minWidth: "40px" }}>
            <MoreHorizOutlined fontSize="small" />
        </Button>
    );
};